import { FirmAdminSidebarNavigationServiceImpl } from './admin/services/FirmAdminSidebarNavigationService/impl';
import { ThinkAlphaQueryClientImpl } from './lib/config/query-client';
import { AnalystRatingsModelImpl } from './models/AnalystRatingsModel/impl';
import { LightweightChartModelImpl } from './models/LightweightChartModel/impl';
import { LightweightChartBarDataServiceImpl } from './services/LightweightChartBarDataService/impl';
import { OptionsListServiceImpl } from './services/OptionsListService/impl';
import { ProxyClientServiceImpl } from './services/ProxyClientService/impl';
import { RecentlyUsedSymbolsServiceImpl } from './services/RecentlyUsedSymbolsService/impl';
import { TradingSocketServiceImpl } from './services/TradingSocketService/impl';
import { TradingSocketProvider } from './services/TradingSocketService/TradingSocketProvider';
import { TradingViewDatafeedServiceImpl } from './services/TradingViewDatafeedService/impl';
import type { DefaultError, QueryObserverOptions } from '@tanstack/query-core';
import type { TypedContainer } from '@thinkalpha/common/ioc/TypedContainer.js';
import { AccessOverviewModelImpl } from 'src/admin/models/AccessOverviewModel/impl';
import { ParentFirmAdminContextModelImpl } from 'src/admin/models/ParentFirmAdminContextModel/impl';
import { PoolManagementModelImpl } from 'src/admin/models/PoolManagementModel/impl';
import { RevenueAnalyticsModelImpl } from 'src/admin/models/RevenueAnalyticsModel/impl';
import { SubscriptionAdminModelImpl } from 'src/admin/models/SubscriptionAdminModel/impl';
import { webOAuth2Module } from 'src/bootstrap/iocModules/webOAuth2Module';
import { AssistantFeatureModule } from 'src/features/assistant/module';
import { CodeEditorFeatureModule } from 'src/features/code-editor/module';
import { FeatureFlagsDevToolsModelImpl } from 'src/features/featureFlagsDevTools/FeatureFlagsDevToolsModel/impl';
import { HelpSidebarModelImpl } from 'src/features/help/HelpSidebarModel/impl';
import { NLPEditorFeatureModule } from 'src/features/nlp/module';
import { AccountsWidgetModelImpl } from 'src/models/AccountsWidgetModel/impl';
import { ActiveUserModelImpl } from 'src/models/ActiveUserModel/impl';
import { ConditionAlertRowViewModelImpl } from 'src/models/AlertsWidgetModel/AlertRowViewModel/impl';
import { IdeaAlertRowViewModelImpl } from 'src/models/AlertsWidgetModel/IdeaAlertRowViewModel/impl';
import { AlertsWidgetModelImpl } from 'src/models/AlertsWidgetModel/impl';
import { AllowedWidgetsModelImpl } from 'src/models/AllowedWidgetsModel/impl';
import { AlphaEyeWidgetModelImpl } from 'src/models/AlphaEyeWidgetModel/impl';
import { BacktestWidgetModelImpl } from 'src/models/BacktestWidgetModel/impl';
import { BasketTradingWidgetModelImpl } from 'src/models/BasketTradingWidgetModel/impl';
import { BillingModelImpl } from 'src/models/BillingModel/impl';
import { BlotterWidgetModelImpl } from 'src/models/BlotterWidgetModel/impl';
import { ChannelSliceImpl } from 'src/models/ChannelSlice/impl';
import { CodeDocumentModelImpl } from 'src/models/CodeDocumentModel/impl';
import { ColumnOptionsSliceImpl } from 'src/models/ColumnOptionsSlice/impl';
import { CurrentUserModelImpl } from 'src/models/CurrentUserModel/impl';
import { EventsWidgetModelImpl } from 'src/models/EventsWidgetModel/impl';
import { ExecutionsWidgetModelImpl } from 'src/models/ExecutionsWidgetModel/impl';
import { FastIdeasSelectorImpl } from 'src/models/FastIdeasSelector/impl';
import { FilingsWidgetModelImpl } from 'src/models/FilingsWidgetModel/impl';
import { ImportsManagerModelImpl } from 'src/models/ImportsManagerModel/impl';
import { LibraryModelImpl } from 'src/models/LibraryModel/impl';
import { LocatesV2WidgetModelImpl } from 'src/models/LocatesV2WidgetModel/impl';
import { LocatesWidgetModelImpl } from 'src/models/LocatesWidgetModel/impl';
import { MessagesWidgetModelImpl } from 'src/models/MessagesWidgetModel/impl';
import { NewsWidgetModelImpl } from 'src/models/NewsWidgetModel/impl';
import { NotepadWidgetModelImpl } from 'src/models/NotepadWidgetModel/impl';
import { OrdersWidgetModelImpl } from 'src/models/OrdersWidgetModel/impl';
import { PaginationDataModelImpl } from 'src/models/PaginationDataModel/impl';
import { PlatformsWidgetModelImpl } from 'src/models/PlatformsWidgetModel/impl';
import { PositionsWidgetModelImpl } from 'src/models/PositionsWidgetModel/impl';
import type { QueryObserverModel } from 'src/models/QueryObserverModel';
import { QueryObserverModelImpl } from 'src/models/QueryObserverModel/impl';
import { ResultsAreaSliceImpl } from 'src/models/ResultsAreaSlice/impl';
import { ResultsWidgetModelImpl } from 'src/models/ResultsWidgetModel/impl';
import { ScannerWidgetModelImpl } from 'src/models/ScannerWidgetModel/impl';
import { ScreenerWidgetModelImpl } from 'src/models/ScreenerWidgetModel/impl';
import { SearchAlphaWidgetModelImpl } from 'src/models/SearchAlphaWidgetModel/impl';
import { SlowIdeasSelectorImpl } from 'src/models/SlowIdeasSelector/impl';
import { StickyFilterSliceImpl } from 'src/models/StickyFilterSlice/impl';
import { SubscriptionPromptModelImpl } from 'src/models/SubscriptionPromptModel/impl';
import { SubscriptionSetupModelImpl } from 'src/models/SubscriptionSetupModel/impl';
import { SymbolsWidgetModelImpl } from 'src/models/SymbolsWidgetModel/impl';
import { TableModelImpl } from 'src/models/TableModel/impl';
import { TableWidgetModelImpl } from 'src/models/TableWidgetModel/impl';
import { TradeProWidgetModelImpl } from 'src/models/TradeProWidgetModel/impl';
import { TradersWidgetModelImpl } from 'src/models/TradersWidgetModel/impl';
import { TradingViewChartWidgetModelImpl } from 'src/models/TradingViewChartWidgetModel/impl';
import { UnavailableTabModelImpl } from 'src/models/UnavailableTabModel/impl';
import { WatchListWidgetModelImpl } from 'src/models/WatchListWidgetModel/impl';
import { WidgetDataModelImpl } from 'src/models/WidgetDataModel/impl';
import { WidgetTabSliceImpl } from 'src/models/WidgetTabSlice/impl';
import { WorkspaceWidgetDataModelImpl } from 'src/models/WorkspaceWidgetDataModel/impl';
import { ConditionAlertModelImpl } from 'src/services/AlertService/AlertModel/impl';
import { AlertServiceProvider } from 'src/services/AlertService/AlertServiceProvider';
import { IdeaAlertModelImpl } from 'src/services/AlertService/IdeaAlertModel/impl';
import { AlertServiceImpl } from 'src/services/AlertService/impl';
import { ColumnOptionsServiceImpl } from 'src/services/ColumnOptionsService/impl';
import { CommandPaletteServiceImpl } from 'src/services/CommandPaletteService/impl';
import { ConfigServiceImpl } from 'src/services/ConfigService/impl';
import { DefaultUniverseServiceImpl } from 'src/services/DefaultUniverseService/impl';
import { FeatureFlagServiceImpl } from 'src/services/FeatureFlagService/impl';
import { FormulaServiceImpl } from 'src/services/FormulaService/impl';
import { KeybindingsServiceImpl } from 'src/services/KeybindingsService/impl';
import { LocalWidgetStorageServiceImpl } from 'src/services/LocalWidgetStorageService/impl';
import { LoginScreenServiceImpl } from 'src/services/LoginScreenService/impl';
import { MarketDataGroupServiceImpl } from 'src/services/MarketDataGroupService/impl';
import { MarketDataServiceImpl } from 'src/services/MarketDataService/impl';
import { MessagesServiceImpl } from 'src/services/MessagesService/impl';
import { PositionInfoGroupServiceImpl } from 'src/services/PositionInfoGroupService/impl';
import { QueryCacheUserServiceImpl } from 'src/services/QueryCacheUserService/impl';
import { SecurityMasterServiceImpl } from 'src/services/SecurityMasterService/impl';
import { ShortcutServiceImpl } from 'src/services/ShortcutService/impl';
import { SlugMapServiceImpl } from 'src/services/SlugMapService/impl';
import { SuggestionsServiceImpl } from 'src/services/SuggestionsService/impl';
import { SymbolNotesServiceImpl } from 'src/services/SymbolNotesService/impl';
import { SyncInternalConfigurationsServiceImpl } from 'src/services/SyncInternalConfigurationsService/impl';
import { SyncToExternalsServiceImpl } from 'src/services/SyncToExternalsService/impl';
import { container as staticContainer } from 'src/StaticContainer';
import type { ReactBindings } from 'src/types/bindings';

export type ReactContainer = TypedContainer<ReactBindings>;

/**
 * Creates the inversify container used by the client and binds all the services.
 *
 * Note that most services you bind here also need to be bound in the electron main container.
 * @see /projects/web/main/ioc/initMainContainer.ts
 */
export const initReactContainer = (): ReactContainer => {
    const container = staticContainer.createChild<ReactBindings>();

    /*
     * Modules and values
     */
    container.bind('QueryClient').to(ThinkAlphaQueryClientImpl).inSingletonScope();
    container.bind('ProxyClientService').to(ProxyClientServiceImpl).inSingletonScope();
    container.bind('StoreProvider').toProvider<ReactBindings['Store']>((context) => () => {
        return Promise.resolve((context.container as any as ReactContainer).get('Store'));
    });
    container.load(AssistantFeatureModule);
    container.load(CodeEditorFeatureModule);
    container.load(NLPEditorFeatureModule);
    container.load(webOAuth2Module);
    container
        .bind('QueryObserverModelFactory')
        .toDynamicValue((context) => {
            return function initQueryObserverModel<TQueryFnData, TError = DefaultError, TQueryData = TQueryFnData>(
                queryOptions: QueryObserverOptions<TQueryFnData, TError, TQueryData>,
            ) {
                const model = context.container.get('QueryObserverModel') as QueryObserverModel<
                    TQueryFnData,
                    TError,
                    TQueryData
                >;
                model.init(queryOptions);

                return model;
            };
        })
        .inSingletonScope();
    container
        .bind('DefaultUniverseServiceProvider')
        .toProvider<ReactBindings['DefaultUniverseService']>((context) => async () => {
            const service = (context.container as any as ReactContainer).get('DefaultUniverseService');
            // Wait for the default universe to have been fetched before returning the service
            await service.initialize();
            return service;
        });
    container.bind('SlugMapServiceProvider').toProvider<ReactBindings['SlugMapService']>((context) => async () => {
        const service = (context.container as any as ReactContainer).get('SlugMapService');
        // Wait for the slug map to have been fetched before returning the service
        await service.initialize();
        return service;
    });

    /*
     * Services
     */
    container.bind('AlertServiceProvider').to(AlertServiceProvider).inSingletonScope();
    container.bind('AlertService').to(AlertServiceImpl).inSingletonScope();
    container.bind('ColumnOptionsService').to(ColumnOptionsServiceImpl).inSingletonScope();
    container.bind('FeatureFlagService').to(FeatureFlagServiceImpl).inSingletonScope();
    container.bind('ConfigService').to(ConfigServiceImpl).inSingletonScope();
    container.bind('DefaultUniverseService').to(DefaultUniverseServiceImpl).inSingletonScope();
    container.bind('FormulaService').to(FormulaServiceImpl).inSingletonScope();
    container.bind('LoginScreenService').to(LoginScreenServiceImpl).inSingletonScope();
    container
        .bind('MarketDataGroupService')
        .toDynamicValue((context) => {
            // Create a new instance each time by manually resolving dependencies
            const tableModel = context.container.get('TableModel');
            const logger = context.container.get('Logger');
            const activeUserModel = context.container.get('ActiveUserModel');
            return new MarketDataGroupServiceImpl(tableModel, logger, activeUserModel);
        })
        .inTransientScope();
    container
        .bind('PositionInfoGroupServiceProvider')
        .toProvider<ReactBindings['PositionInfoGroupService']>((context) => (accountIds: string[]) => {
            // Create a new instance with two TableModels (for pnl and position), logger, and accountIds
            const pnlTableModel = (context.container as unknown as ReactContainer).get('TableModel');
            const positionTableModel = (context.container as unknown as ReactContainer).get('TableModel');
            const logger = (context.container as unknown as ReactContainer).get('Logger');
            return Promise.resolve(
                new PositionInfoGroupServiceImpl(pnlTableModel, positionTableModel, logger, accountIds),
            );
        });
    container.bind('MarketDataService').to(MarketDataServiceImpl).inSingletonScope();
    container.bind('SecurityMasterService').to(SecurityMasterServiceImpl).inSingletonScope();
    container.bind('OptionsListService').to(OptionsListServiceImpl).inSingletonScope();
    container.bind('SlugMapService').to(SlugMapServiceImpl).inSingletonScope();
    container.bind('QueryCacheUserService').to(QueryCacheUserServiceImpl).inSingletonScope();
    container.bind('SyncInternalConfigurationsService').to(SyncInternalConfigurationsServiceImpl).inSingletonScope();
    container.bind('SyncToExternalsService').to(SyncToExternalsServiceImpl).inSingletonScope();
    container.bind('SuggestionsService').to(SuggestionsServiceImpl).inSingletonScope();
    container.bind('LocalWidgetStorageService').to(LocalWidgetStorageServiceImpl).inSingletonScope();
    container.bind('SymbolNotesService').to(SymbolNotesServiceImpl).inSingletonScope();
    container.bind('CommandPaletteService').to(CommandPaletteServiceImpl).inSingletonScope();
    container.bind('KeybindingsService').to(KeybindingsServiceImpl).inSingletonScope();
    container.bind('ShortcutService').to(ShortcutServiceImpl).inSingletonScope();
    container.bind('MessagesService').to(MessagesServiceImpl).inSingletonScope();
    container.bind('RecentlyUsedSymbolsService').to(RecentlyUsedSymbolsServiceImpl).inSingletonScope();
    if (typeof SharedWorker !== 'undefined') {
        container.bind('TradingSocketProvider').to(TradingSocketProvider).inTransientScope();
    } else {
        container.bind('TradingSocketProvider').to(TradingSocketProvider).inSingletonScope();
    }
    container.bind('TradingSocketService').to(TradingSocketServiceImpl).inTransientScope();
    container.bind('LightweightChartBarDataService').to(LightweightChartBarDataServiceImpl).inTransientScope();
    container.bind('LightweightChartModel').to(LightweightChartModelImpl).inTransientScope();
    container.bind('AnalystRatingsModel').to(AnalystRatingsModelImpl).inTransientScope();
    container.bind('TradingViewDatafeedService').to(TradingViewDatafeedServiceImpl).inTransientScope();

    /**
     * Admin Services
     */
    container.bind('FirmAdminSidebarNavigationService').to(FirmAdminSidebarNavigationServiceImpl);

    /**
     * Dev Tools
     */
    container.bind('FeatureFlagsDevToolsModel').to(FeatureFlagsDevToolsModelImpl).inSingletonScope();
    container.bind('HelpSidebarModel').to(HelpSidebarModelImpl).inSingletonScope();

    /*
     * Models
     */
    container.bind('AccountsWidgetModel').to(AccountsWidgetModelImpl);
    container.bind('ActiveUserModel').to(ActiveUserModelImpl).inSingletonScope();
    container.bind('AlertsWidgetModel').to(AlertsWidgetModelImpl);
    container.bind('BacktestWidgetModel').to(BacktestWidgetModelImpl);
    container.bind('ConditionAlertRowViewModelImpl').to(ConditionAlertRowViewModelImpl);
    container
        .bind('ConditionAlertRowViewModelFactory')
        .toDynamicValue((context) => {
            return () => context.container.get('ConditionAlertRowViewModelImpl');
        })
        .inSingletonScope();
    container.bind('ConditionAlertModelImpl').to(ConditionAlertModelImpl);
    container
        .bind('ConditionAlertModelFactory')
        .toDynamicValue((context) => {
            return () => context.container.get('ConditionAlertModelImpl');
        })
        .inSingletonScope();
    container.bind('IdeaAlertModelImpl').to(IdeaAlertModelImpl);
    container
        .bind('IdeaAlertModelFactory')
        .toDynamicValue((context) => {
            return () => context.container.get('IdeaAlertModelImpl');
        })
        .inSingletonScope();
    container.bind('IdeaAlertRowViewModelImpl').to(IdeaAlertRowViewModelImpl);
    container
        .bind('IdeaAlertRowViewModelFactory')
        .toDynamicValue((context) => {
            return () => context.container.get('IdeaAlertRowViewModelImpl');
        })
        .inSingletonScope();
    container.bind('AlphaEyeWidgetModel').to(AlphaEyeWidgetModelImpl);
    container.bind('ResultsWidgetModel').to(ResultsWidgetModelImpl);
    container.bind('BlotterWidgetModel').to(BlotterWidgetModelImpl);
    container.bind('CodeDocumentModel').to(CodeDocumentModelImpl);
    container.bind('ChannelSlice').to(ChannelSliceImpl);
    container.bind('EventsWidgetModel').to(EventsWidgetModelImpl);
    container.bind('AllowedWidgetsModel').to(AllowedWidgetsModelImpl).inSingletonScope();
    container.bind('CurrentUserModel').to(CurrentUserModelImpl).inSingletonScope();
    container.bind('ExecutionsWidgetModel').to(ExecutionsWidgetModelImpl);
    container.bind('ColumnOptionsSlice').to(ColumnOptionsSliceImpl);
    container.bind('FastIdeasSelector').to(FastIdeasSelectorImpl);
    container.bind('SlowIdeasSelector').to(SlowIdeasSelectorImpl);
    container.bind('StickyFilterSlice').to(StickyFilterSliceImpl);
    container.bind('FilingsWidgetModel').to(FilingsWidgetModelImpl);
    container.bind('ImportsManagerModel').to(ImportsManagerModelImpl);
    container.bind('LibraryModel').to(LibraryModelImpl);
    container.bind('LocatesWidgetModel').to(LocatesWidgetModelImpl);
    container.bind('LocatesV2WidgetModel').to(LocatesV2WidgetModelImpl);
    container.bind('MessagesWidgetModel').to(MessagesWidgetModelImpl);
    container.bind('NewsWidgetModel').to(NewsWidgetModelImpl);
    container.bind('NotepadWidgetModel').to(NotepadWidgetModelImpl);
    container.bind('PlatformsWidgetModel').to(PlatformsWidgetModelImpl);
    container.bind('TradeProWidgetModel').to(TradeProWidgetModelImpl);
    container.bind('TradingViewChartWidgetModel').to(TradingViewChartWidgetModelImpl);
    container.bind('TradersWidgetModel').to(TradersWidgetModelImpl);
    container.bind('BasketTradingWidgetModel').to(BasketTradingWidgetModelImpl);
    container.bind('AccessOverviewModel').to(AccessOverviewModelImpl);
    container.bind('PoolManagementModel').to(PoolManagementModelImpl);
    container.bind('RevenueAnalyticsModel').to(RevenueAnalyticsModelImpl);
    container.bind('BillingModel').to(BillingModelImpl).inSingletonScope();
    container.bind('OrdersWidgetModel').to(OrdersWidgetModelImpl);
    container.bind('PaginationDataModel').to(PaginationDataModelImpl);
    container.bind('ParentFirmAdminContextModel').to(ParentFirmAdminContextModelImpl);
    container.bind('PositionsWidgetModel').to(PositionsWidgetModelImpl);
    container.bind('QueryObserverModel').to(QueryObserverModelImpl);
    container.bind('ResultsAreaSlice').to(ResultsAreaSliceImpl);
    container.bind('SearchAlphaWidgetModel').to(SearchAlphaWidgetModelImpl);
    container.bind('SubscriptionAdminModel').to(SubscriptionAdminModelImpl).inSingletonScope();
    container.bind('SubscriptionPromptModel').to(SubscriptionPromptModelImpl).inSingletonScope();
    container.bind('SubscriptionSetupModel').to(SubscriptionSetupModelImpl).inSingletonScope();
    container.bind('ScannerWidgetModel').to(ScannerWidgetModelImpl);
    container.bind('ScreenerWidgetModel').to(ScreenerWidgetModelImpl);
    container.bind('SymbolsWidgetModel').to(SymbolsWidgetModelImpl);
    container.bind('TableModel').to(TableModelImpl);
    container.bind('TableModelProvider').toProvider<ReactBindings['TableModel']>((context) => () => {
        return Promise.resolve(context.container.get('TableModel'));
    });
    container
        .bind('TableModelFactory')
        .toDynamicValue((context) => {
            return () => context.container.get('TableModel');
        })
        .inSingletonScope();
    container.bind('TableWidgetModel').to(TableWidgetModelImpl);
    container.bind('WatchListWidgetModel').to(WatchListWidgetModelImpl);
    container.bind('WidgetDataModel').to(WidgetDataModelImpl);
    container.bind('UnavailableTabModel').to(UnavailableTabModelImpl);
    container.bind('WidgetTabSlice').to(WidgetTabSliceImpl);
    container.bind('WorkspaceWidgetDataModel').to(WorkspaceWidgetDataModelImpl);

    return container;
};
